import type { GridApi, ICellEditorParams } from 'ag-grid-community';
import { isEqual } from 'lodash';
import moment, { Moment } from 'moment';
import React, { Component, createRef, RefObject, useEffect } from 'react';
import styled from 'styled-components';
import { ProjectId } from 'api/models';
import type { ProjectPhase } from 'api/project';
import { TaskAPI } from 'api/tasks';
import { OutsideClickHandler } from 'components/atoms/outside-click-handler';
import { AssigneeFieldEditorPopover } from 'components/organisms/field-editors/assignee/assignee-field-editor-popover';
import { AGGridDateEditor } from 'components/organisms/field-editors/date/ag-grid-date-editor';
import { MultiSelectFieldEditorPopover } from 'components/organisms/field-editors/multi-select/multi-select-field-editor-popover';
import { MultiUserFieldEditorPopover } from 'components/organisms/field-editors/multi-user/multi-user-field-editor-popover';
import { SingleSelectFieldEditorPopover } from 'components/organisms/field-editors/single-select/single-select-field-editor-popover';
import { SingleUserFieldEditorPopover } from 'components/organisms/field-editors/single-user/single-user-field-editor-popover';
import { ClickEventTags, setMarkerForDataDog } from 'hooks/use-data-dog-logger';
import { OzoneFonts } from 'ozone/atoms';
import { useProjectsPhasesQuery } from 'queries';
import type { TaskField } from 'stores/fields';
import type { Task } from 'stores/tasks';
import {
  disabledDueDate,
  disabledStartDate,
} from 'utils/date-field/disabled-dates';
import { getRandomColor } from 'utils/get-random-color';
import { isChildNode } from 'utils/is-child-node';
import { getFieldValue, isSelect, TaskInfoFieldValue } from '../field-value';
import { DurationInputValue } from '../field-value/duration-input';
import {
  AssigneeField,
  DateField,
  FieldType,
  MultiSelectField,
  MultiUserField,
  SingleSelectField,
  SingleUserField,
  SourceType,
} from '../fields';
import type { TaskFieldValue } from '../use-update-tasks';

interface Props extends ICellEditorParams {
  api: GridApi;
  data: Task;
  value: TaskFieldValue;
  onUpdate?: (
    row: Record<string, unknown>,
    field: TaskField,
    from: TaskFieldValue,
    to: TaskFieldValue,
    set: (final: Record<string, unknown>) => void,
  ) => void;
}

const AssigneeEditor = styled(AssigneeFieldEditorPopover)`
  padding: 0 !important;

  & .ozone-select__control {
    outline-offset: -2px !important;
  }
`;

const SingleUserEditor = styled(SingleUserFieldEditorPopover)`
  padding: 0 !important;

  & .ozone-select__control {
    outline-offset: -2px !important;
  }
`;

const MultiUserEditor = styled(MultiUserFieldEditorPopover)`
  padding: 0 !important;

  & .ozone-select__control {
    outline-offset: -2px !important;
  }
`;

const SingleSelectEditor = styled(SingleSelectFieldEditorPopover)`
  padding: 0 !important;
  caret-color: rgba(0, 0, 0, 0);

  & .ozone-select__control {
    outline-offset: -2px !important;
  }
`;

const MultiSelectEditor = styled(MultiSelectFieldEditorPopover)`
  padding: 0 !important;

  & .ozone-select__control {
    outline-offset: -2px !important;
  }
`;

interface State {
  value: TaskField;
  id: string;
  width?: number;
  projectPhases?: ProjectPhase[];
  isLoading?: boolean;
}

const KeyHandler = styled.div`
  display: flex;
  flex: 1;

  & > * {
    display: flex;
    flex: 1;
  }

  & > .ozone-select-container {
    padding: 0;
  }

  .ozone-select__menu {
    margin: 0;
  }

  .ant-picker-dropdown[style] {
    top: 85px !important;
  }
`;

const Wrapper = styled(OutsideClickHandler)`
  display: flex;
  flex: 1;
`;

const getStateValue = (field: TaskField, props: Props) => {
  const { value: propValue } = props;
  let value = propValue;

  if (propValue == null) {
    return null;
  }
  if (field.fieldName === 'status' && typeof propValue === 'object') {
    value = (propValue as any).status as number;
  }
  if (field.fieldName === 'dueDate' && typeof propValue === 'object') {
    value = (propValue as any).dueDate as number;
  }

  return value;
};

export const ConditionedWrapper = (props) => {
  const { data } = props;
  const disabled = data?.field_type === FieldType.Assignee;

  // allowed for multiline text richtext toolbar, and hyperlink form
  const isChild = isChildNode(['ck-toolbar-container', 'ck-link-form']);

  return <Wrapper {...props} allowed={isChild} disabled={disabled} />;
};

const applyReGrouping = (api: GridApi, update: any[]) => {
  setTimeout(() => {
    if (api.getModel().getType() === 'clientSide') {
      api.applyTransaction({
        update,
      });
    }
  }, 10);
};

export const getEditor = (field: TaskField) => {
  const { type } = field;
  const Editor =
    field.fieldName === 'status'
      ? TaskInfoFieldValue
      : field.fieldName === 'effort'
      ? DurationInputValue
      : getFieldValue(type);
  const selectBased = isSelect(type);

  return class extends Component<Props, State> {
    ref: RefObject<any>;

    keyHandlerRef: RefObject<HTMLDivElement>;

    constructor(props: Props) {
      super(props);

      this.state = {
        value: {
          ...field,
          value: getStateValue(field, props),
        } as any,
        id: `editor_${props.node.id}`,
        width:
          type === FieldType.Date
            ? 276
            : type === FieldType.Assignee ||
              type === FieldType.SingleUser ||
              type === FieldType.MultiUser ||
              (type === FieldType.SingleSelect &&
                field.fieldName.toLowerCase() !== 'status') ||
              type === FieldType.MultiSelect
            ? 296
            : undefined,
      };
      this.ref = createRef();
      this.keyHandlerRef = createRef<HTMLDivElement>();
    }

    commit = () => {
      const { node, api, column } = this.props;

      setMarkerForDataDog(ClickEventTags.FieldEdit);

      if (node.id === 'provisional') {
        api.stopEditing();
        if (node.rowIndex != null) {
          api.setFocusedCell(node.rowIndex, column);
        }

        return;
      }
      this.update(true);
    };

    initialise = () => {
      this.focus();
      this.allowNewLineInMultiLine();
    };

    allowNewLineInMultiLine = () => {
      const { id } = this.state;

      if (type !== FieldType.MultiLineText) {
        return;
      }
      const element = document.querySelector(`#${id}`) as HTMLInputElement;

      element?.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.stopPropagation();
        }
      });
    };

    focusInput = () => {
      const { id } = this.state;
      const element = document.querySelector(`#${id}`) as HTMLInputElement;

      element?.focus();
      if (type === FieldType.Boolean) {
        element?.click();
      }
    };

    focusSelect = () => {
      const { ref } = this;

      ref.current?.select?.focus();
    };

    focus = () => {
      const { node } = this.props;
      const { ref } = this;

      if (
        type === FieldType.Date ||
        type === FieldType.Assignee ||
        type === FieldType.SingleUser ||
        type === FieldType.MultiUser ||
        (type === FieldType.SingleSelect &&
          field.fieldName.toLowerCase() !== 'status') ||
        type === FieldType.MultiSelect
      ) {
        ref.current?.focus?.();
      } else if (selectBased && node.id !== 'provisional') {
        this.focusSelect();
      } else {
        this.focusInput();
      }
    };

    getValue = () => {
      const { value } = this.state;

      return value.value;
    };

    onChange = (latest: TaskFieldValue | undefined) => {
      const isSingleEditField =
        type === FieldType.SingleSelect ||
        type === FieldType.SingleUser ||
        type === FieldType.Date ||
        type === FieldType.Boolean ||
        field.fieldName === 'effort';

      if (isSingleEditField) {
        setMarkerForDataDog(ClickEventTags.FieldEdit);
      }

      const {
        value: { value: current },
      } = this.state;
      const { node } = this.props;
      const normalized = current == null ? null : current;

      if (
        latest === normalized &&
        !(node.id === 'provisional' && field.fieldName === 'status')
      ) {
        return;
      }
      this.setState(
        ({ value }) =>
          ({
            value: {
              ...value,
              value: latest == null ? null : latest,
            },
          } as any),
        () => {
          if (isSingleEditField) {
            this.update(true);
          }
        },
      );
    };

    update = (stopEditing?: boolean, callback?: VoidFunction) => {
      const {
        value: { value: current },
      } = this.state;
      const { node, value: previous, api, column, data, onUpdate } = this.props;

      applyReGrouping(api, [{ ...data, [field.fieldName]: current }]);
      // If previous and current input are same irrespective of field type, dont update the field
      // For effort field, even if u chumma click on the cell and click outside again, the value will be set to -1 (if the initial value was null)
      // So for effort field alone, separate handling must be done
      if (field?.fieldName !== 'effort') {
        if (!isEqual(previous ?? null, current ?? null)) {
          onUpdate?.(data, field, previous, (current ?? null) as any, () => {
            callback?.();
          });
          node.updateData({ ...data, [field.fieldName]: current });
        }
      } else if (
        !isEqual(previous ?? null, current ?? null) ||
        (previous == null && current === -1)
      ) {
        onUpdate?.(data, field, previous, (current ?? null) as any, () => {
          callback?.();
        });
        node.updateData({ ...data, [field.fieldName]: current });
      }

      if (stopEditing && node.id !== 'provisional') {
        api.stopEditing();
        if (node.rowIndex != null) {
          api.setFocusedCell(node.rowIndex, column);
        }
      }
    };

    afterGuiAttached = () => {
      this.initialise();
    };

    onAssigneeEditorClose = () => {
      const { api, node, data } = this.props;

      node.updateData({ ...data });
      api.stopEditing();
    };

    onAssigneeChangesDone = () => {
      this.commit();
    };

    onAssigneeChangesSave = (callback: VoidFunction) => {
      this.update(false, callback);
    };

    getDisabledDate = (current: Moment) => {
      const { data } = this.props;
      const { startDate, dueDate } = data;

      if (field.customField) {
        return false;
      }
      const isStartDatedField = field.fieldName.toLowerCase() === 'startdate';

      if (isStartDatedField && dueDate) {
        return disabledStartDate(current, moment(dueDate));
      }
      if (!isStartDatedField && startDate) {
        return disabledDueDate(current, moment(startDate));
      }

      return false;
    };

    DummyView = ({ projectId }: { projectId: number }) => {
      const { data: projectPhases, isLoading } =
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useProjectsPhasesQuery(projectId);

      // eslint-disable-next-line react-hooks/rules-of-hooks
      useEffect(() => {
        this.setState({
          isLoading,
        });
        if (!isLoading) {
          this.setState({
            projectPhases,
          });
        }
      }, [isLoading, projectPhases]);

      return null;
    };

    refresh(params: Props) {
      const { value } = this.state;

      if (params.value !== value.value) {
        this.setState({
          value: { ...field, value: params.value as any },
        });
      }

      return true;
    }

    render() {
      const { data, node, column } = this.props;
      const column_width = column.getActualWidth();
      const { id, value, width = column_width } = this.state;
      const {
        DummyView,
        onChange,
        ref,
        commit,
        keyHandlerRef,
        onAssigneeEditorClose,
        onAssigneeChangesSave,
        onAssigneeChangesDone,
      } = this;
      const projectId = ProjectId(
        data.project || (data.projectId as unknown as number),
      );
      const { projectPhases, isLoading } = this.state;

      if (value.fieldName === 'projectPhaseId') {
        (value.metaData as any).options = projectPhases
          ? projectPhases.map((e) => ({
              label: e.projectPhaseName,
              value: e.projectPhaseId,
              color: getRandomColor(e.projectPhaseName),
              active: true,
            }))
          : null;
      }
      const { startDate, dueDate } = data;

      return (
        <ConditionedWrapper
          data={{
            column_width: column_width.toString(),
            width: width.toString(),
            field_type: type,
          }}
          width={width}
          onOutsideClick={commit}
          allowed={isChildNode([
            'ant-picker-dropdown',
            'ant-popover-content',
            'popover-multi-select-content',
          ])}
        >
          <DummyView projectId={projectId[1]} />
          <KeyHandler ref={keyHandlerRef}>
            {type === FieldType.Date ? (
              <AGGridDateEditor
                ref={ref}
                title={field.fieldLabel}
                field={value as unknown as DateField}
                onChange={onChange as any}
                disabledDate={this.getDisabledDate}
                data={data}
                allowClear={
                  field.sourceType !== SourceType.Task ||
                  field.fieldName !== 'completedAt'
                }
              />
            ) : type === FieldType.Assignee ? (
              <AssigneeEditor
                ref={ref}
                title={field.fieldLabel}
                field={value as any as AssigneeField}
                onChange={onChange as any}
                onClose={onAssigneeEditorClose}
                onDone={onAssigneeChangesDone}
                onSave={onAssigneeChangesSave}
                projectId={projectId}
                isPrivate={data.private}
                startDate={startDate}
                dueDate={dueDate}
              />
            ) : type === FieldType.SingleUser ? (
              <SingleUserEditor
                ref={ref}
                title={field.fieldLabel}
                field={value as any as SingleUserField}
                onChange={onChange as any}
                projectId={projectId}
                isPrivate={data.private}
              />
            ) : type === FieldType.MultiUser ? (
              <MultiUserEditor
                ref={ref}
                title={field.fieldLabel}
                field={value as any as MultiUserField}
                onChange={onChange as any}
                projectId={projectId}
                isPrivate={data.private}
              />
            ) : field.fieldName === 'phaseName' ? (
              <SingleSelectEditor
                ref={ref}
                title={field.fieldLabel}
                field={value as any as SingleSelectField}
                onChange={onChange as any}
              />
            ) : type === FieldType.SingleSelect &&
              field.fieldName.toLowerCase() !== 'status' ? (
              <SingleSelectEditor
                ref={ref}
                title={field.fieldLabel}
                field={value as any as SingleSelectField}
                onChange={onChange as any}
                isLoading={isLoading}
              />
            ) : type === FieldType.MultiSelect ? (
              <MultiSelectEditor
                ref={ref}
                title={field.fieldLabel}
                field={value as any as MultiSelectField}
                onChange={onChange as any}
              />
            ) : (
              <Editor
                provisional={node.id === 'provisional'}
                id={id}
                ref={ref}
                field={value as any as never}
                type={data.type}
                is_private={data.private}
                project_id={projectId}
                onChange={onChange}
                editable
                onKeyDown={(event) =>
                  ['Enter', 'Tab'].includes(event.key) ? commit() : undefined
                }
                onCreateMultiple={
                  field.fieldName === 'status'
                    ? (tasks) => {
                        TaskAPI.addMultipleTasks(
                          data.project,
                          tasks.map((task) => ({
                            taskName: task as unknown as string,
                          })),
                        );
                      }
                    : undefined
                }
                onCreate={
                  field.fieldName === 'status'
                    ? ({ taskName }) => {
                        onChange?.(taskName);
                      }
                    : undefined
                }
                getPopupContainer={() => keyHandlerRef.current!}
              />
            )}
          </KeyHandler>
          <OzoneFonts />
        </ConditionedWrapper>
      );
    }
  };
};
